import { Link, useParams } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { useFormik } from 'formik';
import { equals } from 'ramda';
import {
  Button,
  Container,
  IconButton,
  Typography,
  Stack,
  CircularProgress,
  Checkbox,
  FormControlLabel,
} from '@mui/material';
import { UploadFile } from '@mui/icons-material';

import { userApi } from 'App';
import { ConfirmationDialog, User, useToast, useToogle } from '@shared';
import { FilesUpload, CsvTable, UploadedFile, ExampleDownload, InvitesUsers } from './components';
import { addUsersSchema } from './schema';
import {
  AddUsersValues,
  ColumnMapping,
  columnMappingOptions,
  convertCsvToUserFormat,
  normalizeInvitesUsers,
} from './helpers';

const initialValues: AddUsersValues = {
  isSkipExistingUsers: true,
  arrayColumnMapping: [],
};

export const AddUsers = ({ onExit, partnerId }: { onExit: () => void; partnerId: string }) => {
  const { id: courseId = '' } = useParams();
  const { isOpen: isOpenConfirm, onToogle: onToogleConfirm } = useToogle();
  const toast = useToast();

  const { mutate: checkExistingUsers, isLoading } = useMutation({
    mutationFn: (data: User[]) => userApi.findExistingUsers(partnerId, courseId, data),
    onSuccess: (data) => {
      setFieldValue('invitedUsers', normalizeInvitesUsers(data as User[]));
    },
  });

  const { mutate: sendInvites, isLoading: isLoadingInvites } = useMutation({
    mutationFn: (data: User[]) => userApi.inviteUsers(partnerId, courseId, data),
    onSuccess: () => {
      toast.success('Users have been added');
      onExit();
    },
  });

  const onSendInvites = (data: User[]) => sendInvites(data);

  const { values, handleChange, handleSubmit, setFieldValue, errors, isValid, handleReset } =
    useFormik({
      initialValues,
      validationSchema: addUsersSchema,
      validateOnBlur: true,
      onSubmit: (values) => {
        // const data = parsedData?.slice(values.isFirstRowHeaders ? 1 : 0);
        const columns = values.arrayColumnMapping.filter((i) => i.column);
        checkExistingUsers(convertCsvToUserFormat({ data: parsedData, columns }));
      },
    });

  const { files, parsedData, isSkipExistingUsers } = values;

  const isUnsavedChanges = equals(values, initialValues);

  const handleApproveConfirm = () => {
    onToogleConfirm();
    onExit();
  };

  const setColumnMapping = (values: ColumnMapping[]) => {
    setFieldValue('arrayColumnMapping', values, true);
  };

  const setParsedData = (values: string[][]) => {
    setFieldValue('parsedData', values);
  };

  const onUpload = (data: File[]) => {
    setFieldValue('files', data);
  };

  return (
    <>
      {!values.invitedUsers ? (
        <form onSubmit={handleSubmit}>
          <Container maxWidth='sm'>
            <Typography variant='h5' fontWeight={400} mb={2}>
              Add users
            </Typography>

            {files && !!files.length && <UploadedFile data={files[0]} onDelete={handleReset} />}
            {parsedData ? (
              <>
                <Stack mb={2} paddingX={1.5}>
                  <FormControlLabel
                    label='Skip sending verification email to existing users'
                    control={
                      <Checkbox
                        name='isSkipExistingUsers'
                        checked={values.isSkipExistingUsers}
                        onChange={handleChange}
                      />
                    }
                  />
                </Stack>
                <CsvTable
                  data={parsedData}
                  error={errors.arrayColumnMapping as string}
                  columnMappingOptions={columnMappingOptions}
                  setColumnMapping={setColumnMapping}
                />
              </>
            ) : (
              <>
                <Typography>
                  Please note, there is a limit for 150 records per each csv file
                </Typography>
                <ExampleDownload />
                <FilesUpload onUpload={onUpload} setParsedData={setParsedData}>
                  <>
                    <IconButton color='primary'>
                      <UploadFile />
                    </IconButton>

                    <Typography variant='subtitle1' display='flex' gap={0.5}>
                      <Link to='#'>
                        <Typography color='primary' variant='subtitle1'>
                          Click to {files?.length ? 'replace' : 'upload'} here
                        </Typography>
                      </Link>
                      or drag and drop
                    </Typography>
                    <Typography variant='body2' color='inherit'>
                      .csv (max. 150 records)
                    </Typography>
                  </>
                </FilesUpload>
              </>
            )}
            <Stack direction='row' spacing={1}>
              <Button onClick={!isUnsavedChanges ? onToogleConfirm : onExit} disabled={isLoading}>
                Cancel
              </Button>
              <Button variant='contained' disabled={!isValid || isLoading} type='submit'>
                {isLoading ? <CircularProgress size={18} color='inherit' /> : 'Continue'}
              </Button>
            </Stack>
          </Container>
        </form>
      ) : (
        <InvitesUsers
          users={values.invitedUsers}
          isSkipExistingUsers={isSkipExistingUsers}
          onCancel={onToogleConfirm}
          onBack={() => setFieldValue('invitedUsers', undefined)}
          onSendInvites={onSendInvites}
          isLoading={isLoadingInvites}
        />
      )}
      <ConfirmationDialog
        isConfirmationOpen={isOpenConfirm}
        title='Unsaved changes'
        text='This page contains unsaved changes. Do you still wish to leave the page?'
        labelButton='Leave'
        toggleConfirmationOpen={onToogleConfirm}
        handleApproveConfirm={handleApproveConfirm}
      />
    </>
  );
};
